import Image from '@klv/atoms/image';
import styled, { css } from 'styled-components';
import VimeoPlayer from 'react-player/vimeo';

export const StyledVimeoPlayer = styled(VimeoPlayer)`
  aspect-ratio: 16 / 9;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

export const PlayButtonWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

export const PlayButtonCanvas = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  transition-property: height, width;
  transition-duration: .25s;
  transition-timing-function: ease-in-out;
  bottom: 0;
  left: 0;
  height: 7.2rem;
  width: 8.6rem;
  padding: 1.8rem 2.5rem;
`;

export const Logo = styled(Image)`
  width: 12rem;
`;

export const PlayButtonTimer = styled.span`
  ${({ theme }) => css`
    font-size: 1.3rem;
    line-height: 1.3;
    opacity: 0.8;
    color: ${theme.colors.white};
    padding-left: 1rem;
    ${theme.fonts.sohne.regular}
    position: absolute;
    left: 6rem;
    transition: opacity .25s ease-in-out;
  `}
`;

export const PlayButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3.6rem;
  width: 3.6rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(6, 2, 3, 0.26);
  position: relative;
  z-index: 1;
  > svg {
    height: 1.8rem;
    width: 1.8rem;
    padding-left: 0.2rem;
    transition-property: height, width;
    transition-duration: .25s;
    transition-timing-function: ease-in-out;
  }
  &:after {
    content: ' ';
    position: absolute;
    width:100%;
    height: 100%;
    -webkit-backdrop-filter: blur(5px);
    backdrop-filter: blur(5px);
    border-radius: 50%;
    z-index: -1;
  }
`;

export const VideoBlur = styled.div<{ isPlaying: boolean; }>`
  ${({ isPlaying }) => css`
    aspect-ratio: 16 / 9;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    display: flex;
    justify-content: center;
    align-items: center;
    transition-property: opacity, transform;
    transition-duration: .3s;
    transition-timing-function: ease-in;
    ${isPlaying ? css`
      opacity: 0;
      pointer-events: none;
    ` : css`
      opacity: 1;
      pointer-events: all;
    `}
    > svg {
      transition: opacity .3s ease-in-out;
      width: 100%;
      max-width: 120px;
      opacity: 1;
    }
    &:before {
      content: '';
      position: absolute;
      height: 100%;
      width: 100%;
      -webkit-backdrop-filter: blur(5px);
      backdrop-filter: blur(5px);
      transition-property: opacity, transform, backdrop-filter;
      transition-duration: .3s;
      transition-timing-function: ease-in;
      z-index: -1;
    }
  `}
`;

export const VideoPoster = styled(Image)`
  aspect-ratio: 16 / 9;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  transform: scale(1);
  transition-property: transform;
  transition-duration: .3s;
  transition-timing-function: ease-in; 
`;

export const VideoContainer = styled.div<{ isPlaying: boolean; }>`
  ${({ isPlaying }) => css`
    aspect-ratio: 16 / 9;
    width: 100%;
    position: relative;
    overflow: hidden;
    ${VideoPoster} {
      opacity: ${isPlaying ? '0' : '1'};
      pointer-events: ${isPlaying ? 'none' : 'all'};
      display: ${isPlaying ? 'none' : 'block'};

    }
  `}
`;

export const CardContents = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 1.25rem 1.55rem 1.7rem;
    color: ${theme.colors.white};
    ${theme.screenSize.s576.mediaQuery} {
      font-size: 2rem;
      padding: 2.1rem 2.6rem 2.9rem;
    }
    ${theme.screenSize.s768.mediaQuery} {
      font-size: 1.8rem;
      padding: 2.45rem 2.9rem 3.25rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      padding: 2rem 2.45rem 2.75rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      padding: 2.3rem 3.2rem 3.2rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      padding: 2.6rem 3.2rem 3.6rem;
    }
  `}
`;

export const Quote = styled.div`
  ${({ theme }) => css`
    ${theme.fonts.sohne.regular}
    font-size: 1.6rem;
    line-height: 1.3;
    padding-bottom: .8rem;
    color: ${theme.colors.white};
    ${theme.screenSize.s576.mediaQuery} {
      font-size: 2rem;
      padding-bottom: 1.3rem;
    }
    ${theme.screenSize.s768.mediaQuery} {
      font-size: 1.8rem;
      padding-bottom: 1.45rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      padding-bottom: 1.2rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      padding-bottom: 1.4rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      font-size: 2rem;
    }
  `}
`;

export const Attribution = styled.div``;

export const AttributionWrapper = styled.div`
  ${({ theme }) => css`
    ${theme.fonts.sohne.light}
    color: ${theme.colors.white};
    opacity: .5;
    line-height: 1.7;
    font-size: 1.1rem;
    padding-bottom: 3.6rem;
    flex-grow: 1;
    ${theme.screenSize.s576.mediaQuery} {
      font-size: 1.4rem;
      padding-bottom: 9.3rem;
    }
    ${theme.screenSize.s768.mediaQuery} {
      padding-bottom: 13.5rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      font-size: 1.3rem;
      padding-bottom: 2.1rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      padding-bottom: 6.5rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      font-size: 1.4rem;
      padding-bottom: 7.4rem;
    }
  `};
`;
export const TagWrapper = styled.div``;
export const Tag = styled.span`
  ${({ theme }) => css`
    ${theme.fonts.sohne.regular}
    font-size: 1.1rem;
    line-height: 1.4;
    display: inline-flex;
    color: ${theme.colors.white};
    background-color: rgba(255,255,255,.05);
    padding: .3rem .6rem;
    border-radius: 100px;
    ${theme.screenSize.s576.mediaQuery} {
      font-size: 1.4rem;
      padding: .5rem 1rem;
    }
    ${theme.screenSize.s768.mediaQuery} {
      font-size: 1.4rem;
      padding: .6rem 1.1rem;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      font-size: 1.2rem;
      padding: .45rem .9rem;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      font-size: 1.3rem;
      padding: .6rem 1.1rem;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      font-size: 1.3rem;
      padding: .6rem 1.2rem;
    }
  `}
`;

export const Card = styled.div`
  ${({ theme }) => css`
    border: 0;
    padding: 0;
    max-width: 60vw;
    border-radius: 7px;
    text-align: left;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    background: rgba(16, 14, 14, 0.2);
    box-shadow: 0;
    transition: box-shadow .25s ease-in-out;
    cursor: pointer;

    &:focus-visible {
      outline: 1px dashed ${theme.colors.white};
      outline-offset: 2px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        box-shadow: 0px 25px 30px rgba(35, 36, 38, 0.5);
        ${VideoPoster} {
          transform: scale(1.1);
        }
        ${VideoBlur} {
          > ${Logo} {
            opacity: 0;
          }
          ${PlayButtonCanvas} {
            width: 100%;
            height: 100%;
          }
          ${PlayButtonTimer} {
            opacity: 0;
          }
          ${PlayButton} {
            height: 7.35rem;
            width: 7.35rem;
            background-color: transparent;
            -webkit-backdrop-filter: blur(5px);
            backdrop-filter: blur(5px);
            > ${Logo} {
              height: 3.6rem;
              width: 3.6rem;
              padding-left: 0.2rem;
            }
          }
          &:before {
            -webkit-backdrop-filter: blur(0);
            backdrop-filter: blur(0);
          }
        }
      }
    }

    ${theme.screenSize.s576.mediaQuery} {
      width: auto;
      border-radius: 12.2px;
    }
    ${theme.screenSize.s768.mediaQuery} {
      width: auto;
      border-radius: 13.5px;
    }
    ${theme.screenSize.s1024.mediaQuery} {
      width: auto;
      border-radius: 11.5px;
    }
    ${theme.screenSize.s1200.mediaQuery} {
      width: auto;
      border-radius: 13.5px;
    }
    ${theme.screenSize.s1366.mediaQuery} {
      width: auto;
      border-radius: 15px;
    }
  `}
`;
